'use client';
import type React from 'react';
import { useEffect, useRef } from 'react';

import { useDevices } from '@/hooks/useDevices';

interface Props {
  stream: MediaStream;
}

type HTMLAudioElementWithSetSinkId = HTMLAudioElement & {
  setSinkId: (id: string) => void;
};

export const Audio: React.FC<
  Props &
    React.DetailedHTMLProps<
      React.VideoHTMLAttributes<HTMLAudioElementWithSetSinkId>,
      HTMLAudioElementWithSetSinkId
    >
> = ({ stream, ...rest }) => {
  const audioElem = useRef<HTMLAudioElementWithSetSinkId>(null);

  const { preferredDevice } = useDevices({ type: 'speaker' });

  useEffect(() => {
    const audioRef = audioElem.current;

    if (stream && audioRef) {
      audioRef.load();
      audioRef.srcObject = stream;
    }

    if (audioRef && preferredDevice) {
      audioRef.setSinkId?.(preferredDevice.deviceId);
    }

    return () => {
      if (audioRef) {
        audioRef.srcObject = null;
      }
    };
  }, [audioElem.current, preferredDevice, stream]);

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <audio autoPlay playsInline controls={false} ref={audioElem} {...rest} />
  );
};
